import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Trash2,
  Eye,
  Pencil,
  Image,
  Video,
  FileText,
  File,
  X,
  Calendar,
  Plus,
  Upload,
  Loader2,
  ExternalLink,
  ImageIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import { sq } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { PostType, PostPriority, PostSection } from '@barrikadave/shared';
import type { IPost, IPostCreate, IPostFilters } from '@barrikadave/shared';
import { postsApi, mediaApi, activityApi, type UploadedMedia } from '../services/api';
import { Copy, Check } from 'lucide-react';
import Badge, { getPriorityBadgeVariant } from '../components/Badge';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';

const POST_TYPES = Object.values(PostType);
const PRIORITIES = Object.values(PostPriority);
const SECTIONS = Object.values(PostSection);

const SECTION_LABELS: Record<PostSection, string> = {
  [PostSection.LAJME]: 'Lajme',
  [PostSection.POSTIME]: 'Postime',
  [PostSection.NARRATIVA]: 'Narrativa',
  [PostSection.PS]: 'PS',
  [PostSection.GALERIA]: 'Galeria',
  [PostSection.KUVEND]: 'Kuvend',
  [PostSection.QEVERI]: 'Qeveri',
};

function getTypeIcon(type: string) {
  switch (type) {
    case PostType.PHOTO:
    case 'IMAGE':
      return Image;
    case PostType.VIDEO:
    case 'VIDEO':
      return Video;
    case PostType.DOCUMENT:
    case 'DOCUMENT':
      return FileText;
    case PostType.MIXED:
      return File;
    default:
      return FileText;
  }
}

function getTypeColor(type: string) {
  switch (type) {
    case PostType.PHOTO:
      return 'text-blue-500';
    case PostType.VIDEO:
      return 'text-purple-500';
    case PostType.DOCUMENT:
      return 'text-amber-500';
    case PostType.MIXED:
      return 'text-emerald-500';
    default:
      return 'text-gray-500';
  }
}

function showsFileUpload(type: PostType) {
  return type !== PostType.TEXT;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface PostFormData {
  section: PostSection;
  type: PostType;
  priority: PostPriority;
  title: string;
  content: string;
  sourceUrl: string;
  showOnHome: boolean;
  sendNotification: boolean;
}

const emptyForm: PostFormData = {
  section: PostSection.LAJME,
  type: PostType.TEXT,
  priority: PostPriority.NORMAL,
  title: '',
  content: '',
  sourceUrl: '',
  showOnHome: false,
  sendNotification: false,
};

export default function PostsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState(() => searchParams.get('priority') ?? '');
  const [sectionFilter, setSectionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortDate, setSortDate] = useState<'latest' | 'oldest'>('latest');
  // Priority sort is client-side only (current page) — no server support yet.
  const [sortPriority, setSortPriority] = useState<'asc' | 'desc' | null>(null);

  const [selectedPost, setSelectedPost] = useState<IPost | null>(null);
  const [deletePost, setDeletePost] = useState<IPost | null>(null);
  const [copiedPostId, setCopiedPostId] = useState<string | null>(null);

  // Create/Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<IPost | null>(null);
  const [postForm, setPostForm] = useState<PostFormData>(emptyForm);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedMedia[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const filters: IPostFilters = {
    page,
    limit,
    sort: sortDate,
    ...(search && { search }),
    ...(priorityFilter && { priority: priorityFilter as PostPriority }),
    ...(sectionFilter && { section: sectionFilter as PostSection }),
  };

  const postsQuery = useQuery({
    queryKey: ['posts', filters],
    queryFn: () => postsApi.getAll(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => postsApi.delete(id),
    onSuccess: () => {
      toast.success('Postimi u fshi me sukses');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setDeletePost(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi fshirja e postimit');
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: IPostCreate) => postsApi.create(data),
    onSuccess: () => {
      toast.success('Postimi u krijua me sukses');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      closeModal();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi krijimi i postimit');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<IPostCreate> }) =>
      postsApi.update(id, data),
    onSuccess: () => {
      toast.success('Postimi u perditesua me sukses');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      closeModal();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi perditesimi');
    },
  });

  useEffect(() => {
    setPage(1);
  }, [search, priorityFilter, sectionFilter]);

  // Sync priority filter changes back to URL (without disturbing other params)
  useEffect(() => {
    const current = searchParams.get('priority') ?? '';
    if (current === priorityFilter) return;
    const next = new URLSearchParams(searchParams);
    if (priorityFilter) next.set('priority', priorityFilter);
    else next.delete('priority');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityFilter]);

  const rawPosts = postsQuery.data?.data ?? [];
  const pagination = postsQuery.data?.pagination;

  // Client-side priority sort for the current page only.
  const PRIORITY_RANK: Record<string, number> = {
    [PostPriority.URGENT]: 3,
    [PostPriority.IMPORTANT]: 2,
    [PostPriority.NORMAL]: 1,
  };
  const posts = sortPriority
    ? [...rawPosts].sort((a, b) => {
        const diff = (PRIORITY_RANK[a.priority] ?? 0) - (PRIORITY_RANK[b.priority] ?? 0);
        return sortPriority === 'asc' ? diff : -diff;
      })
    : rawPosts;

  // Deep-link: open post detail when ?postId= is present in URL
  const postIdParam = searchParams.get('postId');
  useEffect(() => {
    if (!postIdParam) return;
    if (selectedPost?.id === postIdParam) return;
    const found = posts.find((p) => p.id === postIdParam);
    if (found) {
      setSelectedPost(found);
      return;
    }
    // Fallback: fetch directly if not on current page
    let cancelled = false;
    postsApi
      .getById(postIdParam)
      .then((p) => {
        if (!cancelled) setSelectedPost(p);
      })
      .catch(() => {
        // Silent: invalid id or no permission
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postIdParam, posts]);

  function openCreateModal() {
    setEditingPost(null);
    setPostForm(emptyForm);
    setUploadedFiles([]);
    setIsModalOpen(true);
  }

  function openEditModal(post: IPost) {
    setEditingPost(post);
    setPostForm({
      section: post.section,
      type: post.type,
      priority: post.priority,
      title: post.title || '',
      content: post.content || '',
      sourceUrl: post.sourceUrl || '',
      showOnHome: post.showOnHome,
      sendNotification: false,
    });
    setUploadedFiles(
      post.media.map((m) => ({
        id: m.id,
        type: m.type,
        url: m.url,
        filename: m.filename,
        mimeType: m.mimeType,
        size: m.size,
        thumbnailUrl: m.thumbnailUrl,
        order: m.order,
      })),
    );
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditingPost(null);
    setPostForm(emptyForm);
    setUploadedFiles([]);
  }

  async function handleFileUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      const results: UploadedMedia[] = [];
      for (const file of Array.from(files)) {
        const media = await mediaApi.upload(file);
        results.push(media);
      }
      setUploadedFiles((prev) => [...prev, ...results]);
      toast.success(`${results.length} skedar(e) u ngarkuan`);
    } catch (err: any) {
      toast.error(err.message || 'Deshtoi ngarkimi i skedarit');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function removeUploadedFile(id: string) {
    try {
      if (!editingPost) {
        await mediaApi.delete(id);
      }
      setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
    } catch {
      setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (postForm.section === PostSection.POSTIME && !postForm.sourceUrl.trim()) {
      toast.error('Seksioni "Postime" kerkon URL burim');
      return;
    }
    if (postForm.section === PostSection.GALERIA && uploadedFiles.length === 0) {
      toast.error('Galeria duhet te kete te pakten 1 media');
      return;
    }
    if (
      postForm.section !== PostSection.POSTIME &&
      !postForm.title.trim() &&
      !postForm.content.trim() &&
      uploadedFiles.length === 0
    ) {
      toast.error('Vendosni titullin, permbajtjen, ose media');
      return;
    }

    if (editingPost) {
      updateMutation.mutate({
        id: editingPost.id,
        data: {
          title: postForm.title || undefined,
          content: postForm.content || undefined,
          priority: postForm.priority,
          showOnHome: postForm.showOnHome,
        },
      });
    } else {
      const createData: IPostCreate = {
        section: postForm.section,
        type: postForm.type,
        priority: postForm.priority,
        title: postForm.title || undefined,
        content: postForm.content || undefined,
        sourceUrl: postForm.sourceUrl || undefined,
        showOnHome: postForm.showOnHome,
        mediaIds: uploadedFiles.length > 0 ? uploadedFiles.map((f) => f.id) : undefined,
        sendNotification: postForm.sendNotification,
      };
      createMutation.mutate(createData);
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isEdit = !!editingPost;

  function closeSelectedPost() {
    setSelectedPost(null);
    if (searchParams.get('postId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('postId');
      setSearchParams(next, { replace: true });
    }
  }

  function getFileAccept() {
    switch (postForm.type) {
      case PostType.PHOTO:
        return 'image/jpeg,image/png,image/gif,image/webp';
      case PostType.VIDEO:
        return 'video/mp4,video/quicktime,video/x-msvideo';
      case PostType.DOCUMENT:
        return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case PostType.MIXED:
        return 'image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      default:
        return '';
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Postimet</h1>
        <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          <span>Shto Postim</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card sticky top-0 z-20">
        <div className="flex flex-col gap-3">
          {/* Section filter buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSectionFilter('')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                sectionFilter === ''
                  ? 'bg-barrikadave-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Te gjitha
            </button>
            {SECTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setSectionFilter(sectionFilter === s ? '' : s)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  sectionFilter === s
                    ? 'bg-barrikadave-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {SECTION_LABELS[s]}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Kerko postime..."
                className="input-field pl-9"
              />
            </div>
            <div className="relative">
              <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="select-field pr-8">
                <option value="">Prioriteti</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Posts List */}
      {postsQuery.isLoading ? (
        <LoadingSpinner className="py-16" />
      ) : postsQuery.isError ? (
        <div className="card text-center py-16">
          <p className="text-red-500 text-sm">Ndodhi nje gabim gjate ngarkimit.</p>
          <button onClick={() => postsQuery.refetch()} className="btn-secondary mt-3 text-sm">
            Provo perseri
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-gray-400 text-sm">Nuk u gjeten postime.</p>
        </div>
      ) : (
        <div className="card !p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs uppercase text-gray-500">
                  <th className="px-4 py-3 text-left w-20"></th>
                  <th className="px-4 py-3 text-left">Postimi</th>
                  <th className="px-4 py-3 text-left">Seksioni</th>
                  <th className="px-4 py-3 text-left">Formati</th>
                  <th className="px-4 py-3 text-left">
                    <button
                      type="button"
                      onClick={() => {
                        setSortPriority((prev) =>
                          prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc',
                        );
                      }}
                      className="flex items-center gap-1 uppercase hover:text-gray-700"
                    >
                      Prioriteti
                      {sortPriority === 'desc' ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : sortPriority === 'asc' ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronsUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      type="button"
                      onClick={() => setSortDate((prev) => (prev === 'latest' ? 'oldest' : 'latest'))}
                      className="flex items-center gap-1 uppercase hover:text-gray-700"
                    >
                      Autori / Data
                      {sortDate === 'latest' ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronUp className="h-3 w-3" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-right">Veprime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {posts.map((post) => {
                  const TypeIcon = getTypeIcon(post.type);
                  const authorName = `${post.author.firstName} ${post.author.lastName}`;
                  const displayDate = format(
                    new Date(post.publishedAt || post.createdAt),
                    'dd MMM yyyy, HH:mm',
                    { locale: sq },
                  );
                  const firstImage = post.media.find((m) => m.type === 'IMAGE');
                  const description = post.content
                    ? post.content.slice(0, 140) + (post.content.length > 140 ? '...' : '')
                    : '';
                  const sectionLabel = SECTION_LABELS[post.section as PostSection] || post.section;

                  return (
                    <tr
                      key={post.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedPost(post)}
                    >
                      {/* Thumbnail */}
                      <td className="px-4 py-3">
                        {firstImage ? (
                          <div className="relative w-16 h-16 rounded-lg bg-gray-100 overflow-hidden">
                            <img
                              src={firstImage.thumbnailUrl || firstImage.url}
                              alt={post.title || 'Post media'}
                              className="w-full h-full object-cover"
                            />
                            {post.media.length > 1 && (
                              <span className="absolute bottom-0.5 right-0.5 bg-black/60 text-white text-[9px] px-1 rounded flex items-center gap-0.5">
                                <ImageIcon className="h-2 w-2" />
                                {post.media.length}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                            <TypeIcon className={`h-6 w-6 ${getTypeColor(post.type)} opacity-50`} />
                          </div>
                        )}
                      </td>

                      {/* Title + description */}
                      <td className="px-4 py-3 max-w-md">
                        {post.title && (
                          <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                            {post.title}
                          </p>
                        )}
                        {description && (
                          <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{description}</p>
                        )}
                        {post.sourceUrl && (
                          <a
                            href={post.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[11px] text-barrikadave-600 hover:underline mt-0.5"
                          >
                            <ExternalLink className="h-2.5 w-2.5" />
                            <span className="truncate max-w-[200px]">
                              {(() => {
                                try {
                                  return new URL(post.sourceUrl).hostname;
                                } catch {
                                  return post.sourceUrl;
                                }
                              })()}
                            </span>
                          </a>
                        )}
                      </td>

                      {/* Section */}
                      <td className="px-4 py-3">
                        <Badge variant="purple">{sectionLabel}</Badge>
                      </td>

                      {/* Format */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <TypeIcon className={`h-3.5 w-3.5 ${getTypeColor(post.type)}`} />
                          <span className="text-xs text-gray-500">{post.type}</span>
                        </div>
                      </td>

                      {/* Priority */}
                      <td className="px-4 py-3">
                        <Badge variant={getPriorityBadgeVariant(post.priority)}>
                          {post.priority}
                        </Badge>
                      </td>

                      {/* Author + date */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-barrikadave-100 flex items-center justify-center shrink-0">
                            <span className="text-barrikadave-600 font-semibold text-[10px]">
                              {post.author.firstName[0]}
                              {post.author.lastName[0]}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">
                              {authorName}
                            </p>
                            <p className="text-[10px] text-gray-400 truncate">{displayDate}</p>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div
                          className="flex items-center justify-end gap-0.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => openEditModal(post)}
                            title="Ndrysho"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeletePost(post)}
                            title="Fshi"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && (
        <div className="card !p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <p className="text-xs text-gray-500">
                Shfaqen {posts.length} nga {pagination.total} postime
              </p>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400">Rreshta:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-barrikadave-500"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            {pagination.totalPages > 1 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>
      )}

      {/* Post Detail Slide-out */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={closeSelectedPost} />
          <div className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto scrollbar-thin">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-gray-900">Detajet e Postimit</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { openEditModal(selectedPost); closeSelectedPost(); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                  title="Ndrysho"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={closeSelectedPost}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-barrikadave-100 flex items-center justify-center">
                  <span className="text-barrikadave-600 font-semibold text-sm">
                    {selectedPost.author.firstName[0]}{selectedPost.author.lastName[0]}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedPost.author.firstName} {selectedPost.author.lastName}
                  </p>
                  <p className="text-xs text-gray-400">{selectedPost.author.position || selectedPost.author.role}</p>
                </div>
              </div>

              {/* Meta */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span>{format(new Date(selectedPost.publishedAt || selectedPost.createdAt), 'dd MMMM yyyy, HH:mm', { locale: sq })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={getPriorityBadgeVariant(selectedPost.priority)}>{selectedPost.priority}</Badge>
                  <span className="text-xs text-gray-400">{selectedPost.type}</span>
                </div>
              </div>

              {/* Title & Content + Copy */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  {selectedPost.title && <h3 className="text-lg font-semibold text-gray-900">{selectedPost.title}</h3>}
                </div>
                {[PostSection.LAJME, PostSection.PS, PostSection.KUVEND, PostSection.QEVERI, PostSection.GALERIA].includes(selectedPost.section as PostSection) && (
                  <button
                    onClick={() => {
                      const text = [selectedPost.title, selectedPost.content].filter(Boolean).join('\n\n');
                      navigator.clipboard.writeText(text);
                      setCopiedPostId(selectedPost.id);
                      activityApi.logAction('CLIPBOARD_COPY', { postId: selectedPost.id, section: selectedPost.section });
                      setTimeout(() => setCopiedPostId(null), 2000);
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${
                      copiedPostId === selectedPost.id
                        ? 'bg-green-50 text-green-600'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {copiedPostId === selectedPost.id ? (
                      <><Check className="w-3.5 h-3.5" /> U kopjua!</>
                    ) : (
                      <><Copy className="w-3.5 h-3.5" /> Kopjo</>
                    )}
                  </button>
                )}
              </div>
              {selectedPost.content && <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedPost.content}</p>}

              {/* Media */}
              {selectedPost.media.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Media ({selectedPost.media.length})</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedPost.media.map((m) => {
                      const MIcon = getTypeIcon(m.type);
                      return (
                        <div key={m.id} className="rounded-lg overflow-hidden border border-gray-200">
                          {m.type === 'IMAGE' && m.url ? (
                            <a href={m.url} target="_blank" rel="noopener noreferrer" className="block">
                              <img
                                src={m.thumbnailUrl || m.url}
                                alt={m.filename}
                                className="w-full h-32 object-cover"
                              />
                              <div className="p-2">
                                <p className="text-xs text-gray-600 truncate">{m.filename}</p>
                              </div>
                            </a>
                          ) : (
                            <a href={m.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 hover:bg-gray-50">
                              <MIcon className="h-4 w-4 text-gray-400 shrink-0" />
                              <span className="text-xs text-gray-600 truncate">{m.filename}</span>
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200 flex gap-3">
                <button
                  onClick={() => { openEditModal(selectedPost); closeSelectedPost(); }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Pencil className="h-4 w-4" />
                  <span>Ndrysho</span>
                </button>
                <button
                  onClick={() => { setDeletePost(selectedPost); closeSelectedPost(); }}
                  className="btn-danger flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Fshi</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Post Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={isEdit ? `Ndrysho Postimin` : 'Shto Postim te Ri'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Seksioni</label>
              <div className="flex flex-wrap gap-2">
                {SECTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setPostForm((p) => ({ ...p, section: s }))}
                    disabled={isEdit}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      postForm.section === s
                        ? 'bg-barrikadave-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } ${isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {SECTION_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lloji</label>
                <select
                  value={postForm.type}
                  onChange={(e) => setPostForm((p) => ({ ...p, type: e.target.value as PostType }))}
                  className="select-field"
                  disabled={isEdit}
                >
                  {POST_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioriteti</label>
                <select
                  value={postForm.priority}
                  onChange={(e) => setPostForm((p) => ({ ...p, priority: e.target.value as PostPriority }))}
                  className="select-field"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Source URL - shown for POSTIME section */}
            {postForm.section === PostSection.POSTIME && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Burim *</label>
                <input
                  type="url"
                  value={postForm.sourceUrl}
                  onChange={(e) => setPostForm((p) => ({ ...p, sourceUrl: e.target.value }))}
                  className="input-field"
                  placeholder="https://rrb.ps.al/..."
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titulli</label>
              <input
                type="text"
                value={postForm.title}
                onChange={(e) => setPostForm((p) => ({ ...p, title: e.target.value }))}
                className="input-field"
                placeholder="Titulli i postimit"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Permbajtja</label>
              <textarea
                value={postForm.content}
                onChange={(e) => setPostForm((p) => ({ ...p, content: e.target.value }))}
                className="input-field min-h-[100px] resize-y"
                placeholder="Shkruani permbajtjen e postimit..."
                rows={4}
              />
            </div>

            {/* File Upload - shown when type is not TEXT */}
            {showsFileUpload(postForm.type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skedaret
                  {postForm.type === PostType.PHOTO && ' (Foto)'}
                  {postForm.type === PostType.VIDEO && ' (Video)'}
                  {postForm.type === PostType.DOCUMENT && ' (Dokumente)'}
                  {postForm.type === PostType.MIXED && ' (Foto, Video, Dokumente)'}
                </label>

                {/* Upload Area */}
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-barrikadave-400 hover:bg-barrikadave-50/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <div className="flex items-center justify-center gap-2 py-2">
                      <Loader2 className="h-5 w-5 text-barrikadave-500 animate-spin" />
                      <span className="text-sm text-gray-500">Duke ngarkuar...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Klikoni per te ngarkuar skedare</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {postForm.type === PostType.PHOTO && 'JPG, PNG, GIF, WEBP (max 50MB)'}
                        {postForm.type === PostType.VIDEO && 'MP4, MOV, AVI (max 200MB)'}
                        {postForm.type === PostType.DOCUMENT && 'PDF, DOC, DOCX, XLS, XLSX (max 20MB)'}
                        {postForm.type === PostType.MIXED && 'Foto, Video, Dokumente'}
                      </p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={getFileAccept()}
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files)}
                />

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedFiles.map((file) => {
                      const FIcon = getTypeIcon(file.type);
                      return (
                        <div key={file.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                          {file.type === 'IMAGE' && file.url ? (
                            <img src={file.thumbnailUrl || file.url} alt={file.filename} className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                              <FIcon className="h-5 w-5 text-gray-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 truncate">{file.filename}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeUploadedFile(file.id)}
                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Options */}
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={postForm.showOnHome}
                onChange={(e) => setPostForm((p) => ({ ...p, showOnHome: e.target.checked }))}
                className="rounded border-gray-300 text-barrikadave-500 focus:ring-barrikadave-500"
              />
              <span className="text-sm font-medium text-gray-700">Shfaq ne faqen kryesore</span>
            </label>
            {!isEdit && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={postForm.sendNotification}
                  onChange={(e) => setPostForm((p) => ({ ...p, sendNotification: e.target.checked }))}
                  className="rounded border-gray-300 text-barrikadave-500 focus:ring-barrikadave-500"
                />
                <span className="text-sm font-medium text-gray-700">Dergo njoftim push</span>
              </label>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button type="button" onClick={closeModal} className="btn-ghost">Anulo</button>
            <button type="submit" disabled={isSubmitting || isUploading} className="btn-primary">
              {isSubmitting ? 'Duke ruajtur...' : isEdit ? 'Ruaj Ndryshimet' : 'Krijo Postim'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        isOpen={!!deletePost}
        onClose={() => setDeletePost(null)}
        onConfirm={() => deletePost && deleteMutation.mutate(deletePost.id)}
        title="Fshi Postimin"
        message="Jeni te sigurt qe deshironi te fshini kete postim? Kjo veprim nuk mund te kthehet mbrapsht."
        confirmText="Fshi"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
